import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash, Minus, Plus, FileText } from "@phosphor-icons/react";
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function Cart() {
  const { items, setQty, remove, subtotal, clear } = useCart();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((x) => ({ product_id: x.product_id, quantity: x.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  const createQuoteRequest = async () => {
    setLoading(true);
    try {
      await api.post("/quotes", {
        customer_name: "Self",
        items: items.map((x) => ({ product_id: x.product_id, name: x.name, quantity: x.quantity, unit_price: x.price, discount_pct: 0 })),
        tax_pct: 13,
        notes: "Quote request from cart"
      });
      toast.success("Quote request created");
      clear();
      navigate("/quotes");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  const tax = subtotal * 0.13;
  const total = subtotal + tax;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="cart-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Cart</div>
        <h1 className="font-display text-4xl font-black tracking-tight mt-1">Your cart</h1>
      </div>
      {items.length === 0 ? (
        <Card className="p-12 rounded-sm border-border text-center text-muted-foreground" data-testid="cart-empty">
          Your cart is empty. <button onClick={() => navigate("/store")} className="text-[var(--uc-primary)] font-medium ml-1">Browse the store</button>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="rounded-sm border-border lg:col-span-2 p-0 overflow-hidden">
            <table className="w-full text-sm dense">
              <thead className="bg-muted/40 text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr><th>Item</th><th>Qty</th><th className="text-right">Price</th><th className="text-right">Total</th><th></th></tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.product_id} className="border-t border-border" data-testid={`cart-row-${it.product_id}`}>
                    <td>
                      <div className="font-medium">{it.name}</div>
                    </td>
                    <td>
                      <div className="inline-flex items-center border border-border rounded-sm">
                        <button className="px-2 py-1" onClick={() => setQty(it.product_id, it.quantity - 1)} data-testid={`qty-dec-${it.product_id}`}><Minus size={12} /></button>
                        <div className="px-3 min-w-[2ch] text-center">{it.quantity}</div>
                        <button className="px-2 py-1" onClick={() => setQty(it.product_id, it.quantity + 1)} data-testid={`qty-inc-${it.product_id}`}><Plus size={12} /></button>
                      </div>
                    </td>
                    <td className="text-right">${it.price.toFixed(2)}</td>
                    <td className="text-right font-medium">${(it.price * it.quantity).toFixed(2)}</td>
                    <td className="text-right">
                      <button className="p-2 text-muted-foreground hover:text-destructive" onClick={() => remove(it.product_id)} data-testid={`remove-${it.product_id}`}><Trash size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <Card className="p-6 rounded-sm border-border space-y-4 h-fit">
            <div className="font-display font-bold text-lg">Order summary</div>
            <div className="text-sm space-y-2">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tax (HST 13%)</span><span>${tax.toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-border pt-2 mt-2 font-display font-bold text-lg"><span>Total</span><span>${total.toFixed(2)} CAD</span></div>
            </div>
            <Button onClick={checkout} disabled={loading} className="w-full rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white h-11" data-testid="checkout-btn">
              {loading ? "Redirecting…" : "Checkout with Stripe"}
            </Button>
            <Button onClick={createQuoteRequest} disabled={loading} variant="outline" className="w-full rounded-sm h-11" data-testid="quote-request-btn">
              <FileText size={16} className="mr-2" /> Convert to quote
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}
